// src\components\FormFail.js

import React, { useState, useEffect } from "react";
import { 
  Button, 
  Typography, 
  Box, 
  Paper,
  Alert,
  AlertTitle,
  Divider
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import HomeIcon from "@mui/icons-material/Home";
import SupportIcon from "@mui/icons-material/Support";
import localApi from "../externalApi/local.js";
import { useLocation } from "react-router-dom";

// Логотип компании из файла favicon.svg
const CompanyLogo = () => (
  <img src="/favicon.svg" alt="Логотип компании" width="32" height="32" />
);

// Тема с фирменными цветами
const themeColors = {
  primary: '#af3831',
  primaryLight: '#c44940',
  primaryDark: '#963029',
  hover: 'rgba(175, 56, 49, 0.04)',
  focus: 'rgba(175, 56, 49, 0.12)'
};

const FormFail = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const user_id = queryParams.get('user_id');
  const transaction_id = queryParams.get('transaction_id');
  
  const [transactionData, setTransactionData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Вспомогательная функция для отправки сообщений через editMessage
  const sendTelegramMessage = async (message) => {
    try {
      await localApi.editMessage(user_id, message);
      console.log(`✅ Отправлено сообщение в Telegram: ${message}`);
    } catch (error) {
      console.error(`❌ Ошибка отправки сообщения в Telegram: ${error.message}`);
    }
  };

  useEffect(() => {
    // Проверяем наличие обязательных параметров
    if (!user_id || !transaction_id) {
      setErrorMessage("Отсутствуют необходимые параметры запроса.");
      // Отправляем сообщение об ошибке параметров
      sendTelegramMessage("Ошибка платежа: отсутствуют необходимые параметры запроса");
      return;
    }

    // Получаем данные о транзакции
    const getTransactionData = async () => {
      try {
        // Получаем данные транзакции
        const transaction = await localApi.getLatestTransaction(user_id);
        
        // Проверяем, совпадает ли ID транзакции
        if (transaction.id !== parseInt(transaction_id)) {
          setErrorMessage("Неверный идентификатор транзакции.");
          await sendTelegramMessage("Ошибка платежа: неверный идентификатор транзакции");
          return;
        }
        
        setTransactionData(transaction);
        
        // Обновляем статус транзакции, если он еще не указан как неудачный
        if (transaction.status !== "FAILED") {
          await localApi.updateTransaction(transaction_id, {
            status: "FAILED"
          });
          
          // Отправляем сообщение о неудачном платеже с деталями
          let failMessage = "Платеж не завершен";
          if (transaction.operation_id) {
            failMessage += ` (код операции: ${transaction.operation_id})`;
          }
          await sendTelegramMessage(failMessage);
        } else {
          // Если статус уже был FAILED, просто отправляем сообщение о посещении страницы ошибки
          await sendTelegramMessage("Пользователь просматривает страницу неудачного платежа");
        }
      } catch (error) {
        console.error("Ошибка при получении данных транзакции:", error);
        setErrorMessage("Не удалось получить информацию о платеже.");
        
        // Отправляем сообщение об ошибке получения данных транзакции
        await sendTelegramMessage(`Ошибка платежа: не удалось получить информацию о транзакции - ${error.message}`);
      }
    };
    
    getTransactionData();
  }, [user_id, transaction_id]);

  // Функция возврата к форме оплаты
  const handleRetry = async () => {
    // Отправляем сообщение о том, что пользователь решил попробовать снова
    await sendTelegramMessage("Пользователь решил повторить попытку оплаты");
    
    // Получаем параметры из текущего URL (search params)
    const currentParams = new URLSearchParams(window.location.search);
    
    // Извлекаем все нужные параметры
    const paramsToKeep = [
      'companyName', 
      'userName', 
      'telegramName', 
      'emailUser', 
      'phoneUser', 
      'companyCountPeople'
    ];
    
    const newParams = new URLSearchParams();
    
    // Копируем только нужные параметры
    paramsToKeep.forEach(param => {
      if (currentParams.has(param)) {
        newParams.append(param, currentParams.get(param));
      }
    });
    
    // Формируем новый URL для формы оплаты
    const paymentFormUrl = `/payment_form?${newParams.toString()}`;
    window.location.href = paymentFormUrl;
  };

  // Переход на главную страницу
  const goToHomePage = async () => {
    // Отправляем сообщение о том, что пользователь отказался от повторной попытки
    await sendTelegramMessage("Пользователь отказался от повторной попытки оплаты и вернулся на главную");
    window.location.href = "https://apod-lab.ru";
  };

  // Переход в техподдержку
  const handleSupportClick = async () => {
    // Отправляем сообщение о том, что пользователь обратился в поддержку
    await sendTelegramMessage("Пользователь обратился в техническую поддержку по поводу платежа");
    // Переход выполняется браузером автоматически по атрибуту href, дополнительного кода не требуется
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3, mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          {/* Добавляем логотип к заголовку */}
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
            <CompanyLogo />
            <Typography variant="h5" sx={{ ml: 2 }}>
              Платеж не завершен
            </Typography>
          </Box>
          <ErrorOutlineIcon sx={{ fontSize: 60, mb: 2, color: "#af3831" }} />
          <Typography variant="body1" color="text.secondary">
            К сожалению, при проведении платежа возникли проблемы
          </Typography>
        </Box>
        
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            borderLeft: `4px solid ${themeColors.primary}`,
            '& .MuiAlert-icon': {
              color: themeColors.primary
            },
            '& .MuiAlert-message': {
              color: 'text.primary'
            },
            '& .MuiAlertTitle-root': {
              color: themeColors.primary,
              fontWeight: 'bold'
            }
          }}
        >
          <AlertTitle>Информация о платеже</AlertTitle>
          <Typography variant="body2">
            Платеж не был завершен успешно. Пожалуйста, попробуйте еще раз или свяжитесь с нашей службой поддержки.
          </Typography>
          {transactionData?.operation_id && (
            <Box sx={{ mt: 1, pt: 1, borderTop: "1px dashed rgba(0,0,0,0.1)" }}>
              <Typography variant="body2" fontWeight="medium">
                Код операции: <span style={{ fontFamily: "monospace" }}>{transactionData.operation_id}</span>
              </Typography>
              <Typography variant="body2">
                Укажите этот код при обращении в техническую поддержку
              </Typography>
            </Box>
          )}
        </Alert>
        
        {errorMessage && (
          <Typography variant="body2" sx={{ mb: 3, color: themeColors.primary }}>
            {errorMessage}
          </Typography>
        )}
        
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
          <Button 
            variant="contained"
            startIcon={<ReplayIcon />}
            onClick={handleRetry}
            size="large"
            sx={{ 
              bgcolor: '#af3831', 
              '&:hover': { 
                bgcolor: '#c44940' 
              },
              '&:active': {
                bgcolor: '#963029'
              }
            }}
          >
            Попробовать снова
          </Button>
          
          <Button 
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={goToHomePage}
            sx={{ 
              borderColor: '#af3831', 
              color: '#af3831',
              '&:hover': { 
                borderColor: '#c44940',
                color: '#c44940',
                bgcolor: 'rgba(175, 56, 49, 0.04)'
              }
            }}
          >
            Вернуться на главную
          </Button>
          
          <Button 
            variant="text"
            startIcon={<SupportIcon />}
            href={`https://t.me/APOD_lab${transactionData?.operation_id ? `?start=${transactionData.operation_id}` : ''}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleSupportClick}
            sx={{ 
              color: '#af3831',
              '&:hover': { 
                bgcolor: 'rgba(175, 56, 49, 0.04)'
              }
            }}
          >
            Связаться с технической поддержкой
          </Button>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Если у вас возникли вопросы или вы считаете, что произошла ошибка, 
          пожалуйста, свяжитесь с нашей службой поддержки. При обращении обязательно
          укажите код операции, который показан выше. Это поможет нам быстрее разобраться
          с вашей проблемой.
        </Typography>
        
        {/* Отладочная информация (скрыта) */}
        <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #eee", display: "none" }}>
          <Typography variant="caption" color="text.secondary">
            ID пользователя: {user_id || "—"} | 
            ID транзакции: {transaction_id || "—"} | 
            Статус: {transactionData?.status || "—"}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default FormFail;